import {Col, Icon, Input, notification, Row, Spin} from 'antd';
import autobind from 'autobind-decorator';
import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {connect} from 'redux-scaffolding-ts';
import {withIdentity} from 'src/core/services/authentication';
import {formatMessage} from 'src/core/services/http.service';
import {ItemState, Query} from 'src/core/stores/dataStore';
import {TableModel, TableView} from 'src/core/ui/collections/table';
import Checkbox from "../../../../components/custom-checkbox";
import {CommandResult} from '../../../../core/stores/types';
import {ProjectTemplatesStore, TaskTemplatesStore, TaskTemplateSummary} from '../../../../stores/templates-store';
import InlineProjectTemplateEditor from './project-template-inline-editor';
import ProjectTemplatesColumn from './project-templates-column';
import NewTaskTemplateView from './task-template-new';
import EditableTooltip from '../../../../components/editable-tooltip/tooltip';
import {UserProfileProps, withUserProfile} from "../../../../components/user-profile";
import { BulkInsertUpload } from '../../../../components/bulk-upload/bulkInsert-upload';

// tslint:disable-next-line:no-empty-interface
interface TaskTemplateListViewProps extends RouteComponentProps<{ companyId: string }>, WithTranslation, UserProfileProps {
    companyId: string;
    companyAllowCir: boolean,
    companyAllowCii: boolean,
    refresh: boolean,
    onRefresh: () => void;
    handleResetTableSuccess?: (value: boolean) => void,
    isRefreshTable?: boolean
}

interface TaskTemplateListViewState {
    query: Query;
    isCreationModalOpen: boolean,
    isBusy: boolean;

}

@connect(['taskTemplates', TaskTemplatesStore], ['projectTemplates', ProjectTemplatesStore])
class TaskTemplateListView extends React.Component<TaskTemplateListViewProps, TaskTemplateListViewState> {
    private get taskTemplateStore() {
        return (this.props as any).taskTemplates as TaskTemplatesStore;
    }
    private get projectTemplateStore() {
        return (this.props as any).projectTemplates as ProjectTemplatesStore;
    }

    constructor(props: TaskTemplateListViewProps) {
        super(props);
        this.state = {
            query: { searchQuery: '', orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }], skip: 0, take: 10 },
            isCreationModalOpen: false,
            isBusy: false,
        };
    }

    componentWillMount() {
        this.taskTemplateStore.changeUrl(this.props.companyId);
        this.projectTemplateStore.changeUrl(this.props.companyId);
        this.load(this.state.query);
    }

    @autobind
    private load(query: Query) {
        this.taskTemplateStore.getAllAsync(query);     
    }

    componentDidUpdate() {
        if (this.props.refresh) {
            this.props.onRefresh()
            this.load(this.state.query)
            this.setState({})
        }
    }

    @autobind
    private isReadOnly() {
        return (this.props.userProfile && this.props.userProfile.roles &&
            (this.props.userProfile.roles.includes("CompanyDataViewer")
                && !(this.props.userProfile.roles.includes("Manager")
                    || this.props.userProfile.roles.includes("Administrator")
                    || this.props.userProfile.roles.includes("ClientAdmin")
                    || this.props.userProfile.roles.includes("FIConsultant"))));
    }

    @autobind
    private loadProjectTemplatesTab() {
        this.projectTemplateStore.getAllAsync({ searchQuery: '', orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }], skip: 0, take: 10 });
    }

    @autobind
    private onQueryChanged(query: Query) {
        this.setState({ query }, () => this.load(query));
    }

    @autobind
    private async onDeleteRow(item: TaskTemplateSummary, state: ItemState): Promise<CommandResult<any>> {
        return await this.taskTemplateStore.deleteAsync(`${item.id}`);
    }

    @autobind
    private onNewItem() {
        this.setState({ isCreationModalOpen: true });
    }

    @autobind
    private countDownNotification(_notification: any, message: string, description: string, key: string) {
        let secondsToGo = 4;
        _notification({
            key: key,
            message: message + `(${secondsToGo}).`,
            description: description
        });
        const timer = setInterval(() => {
            secondsToGo -= 1;
            _notification({
                key: key,
                message: message + `(${secondsToGo}).`,
                description: description,
                onClose: () => clearInterval(timer),
            });
        }, 1000);
        setTimeout(() => {
            clearInterval(timer);
            notification.close(key);
        }, secondsToGo * 1000);
    }

    @autobind
    private async onSaveItem(item: TaskTemplateSummary, state: ItemState) {
        const t = this.props.t

        const result = await this.taskTemplateStore.saveAsync(`${item.id}`, item, state)
                .then(result => {
                    this.countDownNotification(notification.success, t("Notification"), t("All changes were successfully saved"), "succes")
                    this.load(this.state.query)
                    return result;
                })
                .catch(error => {
                    this.countDownNotification(notification.error, t("Error"), t("There was an error saving changes"), "error")
                    return error;
                })
        return result;
    }

    @autobind
    private onNewItemClosed(id: string | undefined) {
        this.setState({ isCreationModalOpen: false });
        this.load(this.state.query);
    }

    public render() {
        const { t } = this.props as any;
        let isReadOnly = this.isReadOnly()

        if (this.props.isRefreshTable) {
            this.load(this.state.query);
            if (this.props.handleResetTableSuccess) {
                this.props.handleResetTableSuccess(false);
            }
        }

        const tableModel = {
            query: this.state.query,
            columns: [
                {
                    sortable: true,
                    field: 'title',
                    title: t('Task Template Name'),
                    renderer: (data) => (
                        <span> {data.title} </span>
                    ),
                    editor: (data) => <Input maxLength={255}/>
                },
                {
                    sortable: true,
                    field: 'reference',
                    title: t('Reference'),
                    renderer: (data) => (
                        <span> {data.reference} </span>
                    ),
                    editor: (data) => <Input maxLength={255} />
                },
                {
                    sortable: false,
                    field: 'sticky',
                    title: <EditableTooltip name="template-task-sticky">{t('Sticky')}</EditableTooltip>,
                    align: 'center',
                    renderer: (data) => (
                        <span>{data.sticky && <Icon type="check" />} </span>
                    ),
                    editor: (data) => <Checkbox />
                },
                {
                    sortable: false,
                    field: 'cirEligibility',
                    title: t('CIR Eligibility'),
                    align: 'center',
                    renderer: (data) => (
                        <span style={{ textAlign: "center" }}>{data.cirEligibility && this.props.companyAllowCir && <Icon type="check" />} </span>
                    ),
                    editor: (data) => <Checkbox initialValue={data.cirEligibility && this.props.companyAllowCir} disabled={!this.props.companyAllowCir} />
                },
                {
                    sortable: false,
                    field: 'ciiEligibility',
                    title: t('CII Eligibility'),
                    align:'center',
                    renderer: (data) => (
                        <span> {data.ciiEligibility && this.props.companyAllowCii && <Icon type="check" />} </span>
                    ),
                    editor: (data) => <Checkbox initialValue={data.ciiEligibility && this.props.companyAllowCii } disabled={!this.props.companyAllowCii} />
                },
                {
                    sortable: false,
                    field: 'projectTemplates',
                    title: <EditableTooltip name="template-task-project"> {t('Project Templates')}</EditableTooltip>,
                    renderer: (data) => (
                        <ProjectTemplatesColumn projectTemplates={data.projectTemplates} />
                    ),
                    editor: (data) => <InlineProjectTemplateEditor companyId={this.props.companyId}/>
                }
            ],
            data: this.taskTemplateStore.state,
            sortFields: [{
                field: 'title',
                text: t('Task Template Name'),
                useProfile: false
            }],
        } as TableModel<TaskTemplateSummary>;

        return (
            <div>
                <Row  type={'flex'} align='top' justify='space-between' >
                    <Col span={24} style={{ width: '100%' }}>
                        <Spin data-tut="reactour__navegateTemplate1" size='large' spinning={this.taskTemplateStore.state.isBusy}>
                            <TableView
                                showDeleteStatusWindow
                                rowKey='id'
                                model={tableModel}
                                error={this.taskTemplateStore.state.result && !this.taskTemplateStore.state.result.isSuccess && formatMessage(this.taskTemplateStore.state.result)}
                                embedded={false}
                                onQueryChanged={this.onQueryChanged}
                                onRefresh={() => this.load(this.state.query)}
                                canEdit={!isReadOnly}
                                canDelete={!isReadOnly}
                                onDeleteRow={this.onDeleteRow}
                                canCreateNew={!isReadOnly}
                                onNewItem={this.onNewItem}
                                onSaveRow={this.onSaveItem}
                                onUploadEnd={this.loadProjectTemplatesTab}
                                leftToolbar={
                                    <>
                                        <li>
                                            <BulkInsertUpload
                                                bulkTemplateName={t('tasktemplates.xlsx')}
                                                bulkInsertUrl={(isReadOnly) ? undefined : `api/v1/task-templates/${this.props.match.params.companyId}/bulk_insert`}
                                                bulkTemplateUrl={(isReadOnly) ? undefined : `api/v1/task-templates/${this.props.match.params.companyId}/template`}
                                            />
                                        </li>                                       
                                    </>
                                }>
                            </TableView>
                        </Spin>
                    </Col>
                </Row>
                {this.state.isCreationModalOpen &&
                    <NewTaskTemplateView
                        companyId={this.props.companyId}
                        onClose={this.onNewItemClosed}
                        companyAllowCir={this.props.companyAllowCir}
                        companyAllowCii={this.props.companyAllowCii}
                    />}
            </div>
        )
    }
}

// Wire up the React component to the Redux store
export default withUserProfile(withTranslation()(withIdentity(withRouter(TaskTemplateListView))));
